import { IN_APP, PUSH } from 'constants/messageMediums';
import { selectIsEmbeddedMessagingCoreFeatureFlagOn } from 'core/userContext';
import { InputField } from 'lib/iterable-hook-form/FormField';
import isEmpty from 'lodash/isEmpty';
import { useCreateCampaign } from 'modules/campaignIndex/hooks';
import { getIsEmbeddedMessage } from 'modules/campaignIndex/utils';
import { EditableLabels } from 'modules/campaigns/lib/components/EditableLabels';
import { getIsWhatsAppCampaign } from 'modules/campaigns/lib/utils';
import { CreatePlacementsPrompt } from 'modules/placements/components/CreatePlacementsPrompt';
import { useQueryPlacements } from 'modules/placements/hooks/useQueryPlacements';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { BUTTON_SIZES, Button } from 'ui/button';
import { FormField } from 'ui/formField';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { spacing } from 'ui/theme';
import { getMediumByKey } from 'utils/messageMediums';
import noop from 'utils/noop';
import { useCreateCampaignContext } from '../../campaignFolders/contexts/CreateCampaignContext';
import { Destination } from '../../components/Destination';
import { useCampaignFolderModals } from '../../hooks/useCampaignFolderModals';
import { CampaignType } from './CampaignType';
import { EmbeddedMessageFields } from './EmbeddedMessageFields';

const StyledFormField = styled(FormField)`
  margin-bottom: ${spacing.half};
`;

export const CREATE_CAMPAIGN_MODAL_ID = 'create-campaign-modal-wrapper';

type CreateCampaignModalProps = {
  /** Callback function when modal closes */
  onClose?: () => void;
};

export const CreateCampaignModal = ({ onClose = noop }: CreateCampaignModalProps) => {
  const isEmbeddedMessagingEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const modals = useCampaignFolderModals();
  const { createCampaign, isLoading } = useCreateCampaign(onClose);
  const { messageMedium, hasApprovedBusinessTemplates } = useCreateCampaignContext();
  const { placements } = useQueryPlacements();

  const medium = getMediumByKey(messageMedium)?.adjective;
  const isEmbeddedMessage = getIsEmbeddedMessage(messageMedium);
  const isWhatsAppCampaign = getIsWhatsAppCampaign(messageMedium);
  const shouldShowCreatePlacementsPrompt =
    isEmbeddedMessagingEnabled && isEmbeddedMessage && !placements?.length;

  const {
    getValues,
    handleSubmit,
    formState: { errors },
  } = useFormContext();

  const nameErrorMessage = errors?.name?.message as string | undefined;

  const handleClose = () => {
    modals.createCampaign.toggle(false);
    onClose();
  };

  return (
    <Modal
      data-test={CREATE_CAMPAIGN_MODAL_ID}
      id="create-campaign-modal"
      isModalOpen={true}
      onClose={handleClose}
      withPortal
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader
        autoFocus={false}
        data-test="create-campaign-modal-header"
      >{`New ${medium} campaign`}</ModalHeader>
      {shouldShowCreatePlacementsPrompt ? (
        <ModalBody data-test="create-campaign-modal-body">
          <CreatePlacementsPrompt />
        </ModalBody>
      ) : (
        <>
          <ModalBody data-test="create-campaign-modal-body">
            {/* Campaign name */}
            <StyledFormField
              errorText={nameErrorMessage}
              hasError={!!nameErrorMessage}
              inputId="campaign-name-field"
              labelText="Campaign name"
            >
              <InputField autoFocus id="campaign-name" data-test="campaign-name" name="name" />
            </StyledFormField>

            {/* Campaign labels */}
            <EditableLabels />

            {/* Destination */}
            {(messageMedium === PUSH || messageMedium === IN_APP) && <Destination />}

            {/* Embedded message fields (includes destination and placement) */}
            {isEmbeddedMessage && <EmbeddedMessageFields />}

            {/* Campaign type */}
            <CampaignType
              isEmbeddedMessage={isEmbeddedMessage}
              isWhatsAppCampaign={isWhatsAppCampaign}
              hasApprovedBusinessTemplates={hasApprovedBusinessTemplates}
            />
          </ModalBody>
          <ModalFooter data-test="create-campaign-modal-actions">
            <Button
              // This more accurately accounts for the "on render" default values validation that currently
              // occurs on the schema. Instead of checking isValid which is a proxy for whether there are
              // errors, we're now directly checking if there are errors
              isDisabled={!isEmpty(errors)}
              isLoading={isLoading}
              onClick={handleSubmit(() => createCampaign(getValues()))}
              size={BUTTON_SIZES.LARGE}
            >
              Create campaign
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};
